import React, { useState, useEffect, useRef } from "react";
import { useSelector, useDispatch } from "react-redux";
import { IAppState } from "../../types/stores/index";
import { deleteAllItemsInFlow } from "../../actions/flowActions";
import {
    showModal,
    requestMyCompanies,
    CAPageChangeCompany,
    CAPageChangeCompanyNoUnsavedCheck,
} from "../../actions/actionCreators";

import { Popover, MenuItem, Tooltip } from "@material-ui/core";

interface CompanySelectProps {
    // Props
    isUpdateOnChange?: boolean;
    handleFieldChange?: (newCompanyId: number) => void;
}

const CompanySelectSide: React.FC<CompanySelectProps> = ({
    isUpdateOnChange,
    handleFieldChange,
}: CompanySelectProps) => {
    const dispatch = useDispatch();
    const companies = useSelector((state: IAppState) => state.session.companies || []);
    const selectedCompany = useSelector((state: IAppState) => state.session.companyId);
    const isAuthenticated = useSelector((state: IAppState) => state.session.isAuthenticated);
    const flow = useSelector((state: IAppState) => state.flows.byId[state.selected.flow]);
    const activeConnections = useSelector((state: IAppState) => state.fileImportQueue.activeConnections || 0);
    const prevIsAuthenticatedRef = useRef<boolean>(isAuthenticated);
    const [anchorEl, setAnchorEl] = useState(null);
    const [selectedCompanyName, setSelectedCompanyName] = useState("");
    const [selectedCompanyLogo, setSelectedCompanyLogo] = useState("");
    const [searchTerm, setSearchTerm] = useState("");
    const open = Boolean(anchorEl);

    useEffect(() => {
        if (isAuthenticated && companies.length === 0) {
            requestMyCompanies();
        }
    }, [isAuthenticated, companies]);

    // Handle keypress events
    useEffect(() => {
        const handleKeyPress = event => {
            if (!open) return;
            const char = event.key.toLowerCase();
            if (/^[a-z0-9]$/.test(char)) {
                setSearchTerm(prev => prev + char);
            }

            // Handle ArrowUp and ArrowDown for navigation
            if (event.key === "ArrowDown") {
                event.preventDefault(); // Prevent default scrolling behavior
                const focusedElement = document.activeElement;
                // Check if the focused element is a MenuItem
                if (focusedElement?.classList.contains("company-select-option")) {
                    const nextElement = focusedElement.nextElementSibling as HTMLElement | null;
                    if (nextElement) {
                        nextElement.focus();
                    }
                } else {
                    // If no MenuItem is focused, focus the selected MenuItem
                    const selectedMenuItem = document.querySelector(
                        ".company-select-option.Mui-selected"
                    ) as HTMLElement | null;
                    if (selectedMenuItem) {
                        const nextElement = selectedMenuItem.nextElementSibling as HTMLElement | null;
                        if (nextElement) {
                            nextElement.focus();
                        }
                    } else {
                        // If no selected MenuItem is found, fallback to the first MenuItem
                        const firstMenuItem = document.querySelector(".company-select-option") as HTMLElement | null;
                        if (firstMenuItem) {
                            firstMenuItem.focus();
                        }
                    }
                }
            } else if (event.key === "ArrowUp") {
                event.preventDefault(); // Prevent default scrolling behavior
                const focusedElement = document.activeElement;
                // Check if the focused element is a MenuItem
                if (focusedElement?.classList.contains("company-select-option")) {
                    const previousElement = focusedElement.previousElementSibling as HTMLElement | null;
                    if (previousElement) {
                        previousElement.focus();
                    }
                } else {
                    // If no MenuItem is focused, focus the selected MenuItem
                    const selectedMenuItem = document.querySelector(
                        ".company-select-option.Mui-selected"
                    ) as HTMLElement | null;
                    if (selectedMenuItem) {
                        const previousElement = selectedMenuItem.previousElementSibling as HTMLElement | null;
                        if (previousElement) {
                            previousElement.focus();
                        }
                    } else {
                        // If no MenuItem is focused, focus the last MenuItem
                        const lastMenuItem = document.querySelector(
                            ".company-select-option:last-child"
                        ) as HTMLElement | null;
                        if (lastMenuItem) {
                            lastMenuItem.focus();
                        }
                    }
                }
            }
        };

        if (open) {
            document.addEventListener("keydown", handleKeyPress);
        }

        return () => {
            document.removeEventListener("keydown", handleKeyPress);
        };
    }, [open]);

    // Scroll to the matching item
    useEffect(() => {
        if (searchTerm && open) {
            const matchingItem = document.querySelector(`[data-key^="${searchTerm}"]`) as HTMLElement;

            if (matchingItem) {
                // Focus the MenuItem
                matchingItem.focus();
            }
        }

        // Clear the search term after a short delay
        const timeout = setTimeout(() => setSearchTerm(""), 1000);
        return () => clearTimeout(timeout);
    }, [searchTerm, open]);

    useEffect(() => {
        if (companies && companies.length > 0) {
            const currentCompany = companies.find(company => company.CompanyID === selectedCompany);
            if (currentCompany) {
                setSelectedCompanyName(currentCompany.CompanyName);
                setSelectedCompanyLogo(`/Images/CompanyLogo/${selectedCompany}`);
            }
        }
    }, [companies, selectedCompany]);

    useEffect(() => {
        const prevIsAuthenticated = prevIsAuthenticatedRef.current;
        const isLoggingIn = !prevIsAuthenticated && isAuthenticated;

        if (isLoggingIn) {
            requestMyCompanies();
        }

        prevIsAuthenticatedRef.current = isAuthenticated;
    }, [isAuthenticated]);

    const handleImageLoad = async (event: React.SyntheticEvent<HTMLImageElement, Event>) => {
        const img = event.currentTarget;
        const response = await fetch(img.src);
        const blob = await response.blob();
        const reader = new FileReader();
        reader.onloadend = () => {
            const base64data = reader.result as string;
            const placeholderValue = "R0lGODlhAQABAIAAAAAAAP///yH5BAEAAAAALAAAAAABAAEAAAIBRAA7";
            if (base64data.includes(placeholderValue)) {
                setSelectedCompanyLogo("/Images/Icons/default_logo.png"); // Set fallback image
            }
        };
        reader.readAsDataURL(blob);
    };

    const changeCompany = companyIdValue => {
        const newCompanyId = companyIdValue;
        const oldCompanyId = selectedCompany;

        if (isUpdateOnChange) {
            if (flow != null && flow.hasUnsavedChanges) {
                dispatch(CAPageChangeCompany(newCompanyId, oldCompanyId));
            } else {
                dispatch(deleteAllItemsInFlow(-1));
                dispatch(CAPageChangeCompanyNoUnsavedCheck(newCompanyId));
                // dispatch(setSelectedCompany(newCompanyId));
            }
        }
        if (handleFieldChange) {
            dispatch(handleFieldChange(newCompanyId));
            // dispatch(setSelectedCompany(newCompanyId));
        }
    };

    if (!isAuthenticated) {
        return null;
    }

    const handleClick = e => setAnchorEl(e.currentTarget);
    const handleClose = () => setAnchorEl(null);
    const handleMenuItemClick = companyId => {
        if (activeConnections > 0) {
            showModal("FILE_BIG_SIZE_UPLOAD_WARNING", {
                continue: () => changeCompany(companyId),
                type: "Change Company",
            });
        } else {
            changeCompany(companyId);
        }

        setAnchorEl(null);
    };

    const id = open ? "simple-popover" : undefined;

    const sortedCompanies = [...companies].sort((a, b) => {
        const nameA = a.CompanyName.toLowerCase();
        const nameB = b.CompanyName.toLowerCase();
        if (nameA < nameB) {
            return -1;
        }
        if (nameA > nameB) {
            return 1;
        }
        return 0;
    });

    const options = sortedCompanies.map(company => (
        <MenuItem
            key={company.CompanyID}
            data-key={company.CompanyName.toLowerCase()}
            className="company-select-option"
            value={company.CompanyID}
            selected={company.CompanyID === selectedCompany}
            onClick={() => handleMenuItemClick(company.CompanyID)}
        >
            {company.CompanyName}
        </MenuItem>
    ));

    return (
        <a>
            <Tooltip title={selectedCompanyName} style={{ fontSize: 12 }} placement="right">
                <img
                    className="logo-wayfarer"
                    id={`react_header_logo`}
                    src={`${selectedCompanyLogo}`}
                    alt={`react header logo`}
                    onClick={handleClick}
                    onLoad={handleImageLoad}
                />
            </Tooltip>
            <Popover
                id={id}
                open={open}
                anchorEl={anchorEl}
                onClose={handleClose}
                anchorOrigin={{
                    vertical: "center",
                    horizontal: "right",
                }}
                transformOrigin={{
                    vertical: "top",
                    horizontal: "left",
                }}
                style={{ minWidth: "200px", overflow: "auto", marginLeft: "10px", marginTop: "15px", padding: "200px" }}
            >
                {options}
            </Popover>
        </a>
    );
};

export default CompanySelectSide;
